/**
 * Post Show API
 *
 * Request helpers for post detail interactions such as reads, likes, and edits.
 */

import { ApiBase } from "../../core/api-base";
import { z } from "zod";
import {
    AddLikeToPost201Response,
    DeleteBoardPost200Response,
    GetPostDetail200Response,
    RemoveLikeFromPost200Response,
    UpdateBoardPost200Response,
} from "../../api/generated/posts.zod";
import { AddReadToPost201Response } from "../../api/generated/reads.zod";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { getImageHandlerByPostId } from "../../util/media/image/image-setup";
import { postShowForm } from "./post-show-form";

interface MarkAsReadResult {
    success: boolean;
    readCount: number;
    isFirstRead: boolean;
}

type MarkAsReadResponse = z.infer<typeof AddReadToPost201Response>;
type FetchPostResponse = z.infer<typeof GetPostDetail200Response>;
type LikePostResponse = z.infer<typeof AddLikeToPost201Response>;
type UnlikePostResponse = z.infer<typeof RemoveLikeFromPost200Response>;
type UpdatePostResponse = z.infer<typeof UpdateBoardPost200Response>;
type DeletePostResponse = z.infer<typeof DeleteBoardPost200Response>;
type UpdatePostData = UpdatePostResponse["data"];
type DeletePostData = DeletePostResponse["data"];
type FetchPostData = NonNullable<FetchPostResponse["data"]>["item"];

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
type JsonObject = { [key: string]: JsonValue };
type PostShowApiData = JsonValue | undefined;
type UnknownRecord = Record<string, unknown>;

interface BasicApiEnvelope<TData = PostShowApiData> {
    success?: boolean;
    message?: string;
    data?: TData;
    reads_count?: number;
    is_first_read?: boolean;
}

interface LikesApiData {
    likes_count?: number;
}

interface EditImageFormDataItem {
    name?: string;
    data?: File;
    rotation?: number;
}

interface EditPostData {
    post_title?: string;
    content?: string;
    post_product_id?: number | null;
    created_at?: string;
    post_status?: string;
    sns_links?: string[];
    [key: string]: JsonValue | File[] | string[] | UnknownRecord | undefined;
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

class PostShowApiService extends ApiBase {
    public readRecorded: boolean;

    public constructor() {
        super();
        this.readRecorded = false;
    }

    public async init(): Promise<void> {
        this.readRecorded = false;
    }

    public async markAsRead(postId: number, boardId: string, _isModal = false): Promise<MarkAsReadResult> {
        void _isModal;

        if (this.readRecorded) {
            return {
                success: true,
                readCount: 0,
                isFirstRead: false,
            };
        }

        try {
            const response = await super.apiPost(`/api/v1/posts/${boardId}/${String(postId)}/reads`, null, {
                noSpinner: true,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = (await response.json()) as MarkAsReadResponse;

            if (data.success === 1) {
                this.readRecorded = true;
                return {
                    success: true,
                    readCount: data.reads_count ?? 0,
                    isFirstRead: false,
                };
            }

            throw new Error("Failed to record read");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public setReadRecorded(value = true): void {
        this.readRecorded = Boolean(value);
    }

    public async updatePost(
        boardId: string,
        postId: number,
        editData: EditPostData,
    ): Promise<{ success: boolean; data: UpdatePostData }> {
        const formData = new FormData();
        formData.append("post_title", String(editData.post_title || ""));
        formData.append("content", String(editData.content || ""));

        if (Object.prototype.hasOwnProperty.call(editData, "post_product_id")) {
            formData.append("post_product_id", String(editData.post_product_id ?? ""));
        }

        if (editData.created_at && typeof editData.created_at === "string" && editData.created_at.trim() !== "") {
            formData.append("created_at", editData.created_at.trim());
        }

        // Admin/editor-only field — absent from editData entirely for non-privileged
        // viewers (see extractEditedData() in post-show-handler.ts), so only append
        // when actually present.
        if (editData.post_status && typeof editData.post_status === "string" && editData.post_status.trim() !== "") {
            formData.append("post_status", editData.post_status.trim());
        }

        if (editData.sns_links && Array.isArray(editData.sns_links)) {
            const normalized = SNSLinksUtils.normalizeSnsLinks(editData.sns_links);
            normalized.forEach((link) => {
                if (link) {
                    formData.append("sns_links[]", link);
                }
            });
        }

        const imageHandler = getImageHandlerByPostId(postId);
        if (imageHandler) {
            const images = imageHandler.getFormData() as EditImageFormDataItem[];
            images.forEach((img) => {
                const file = img?.data;
                if (!(file instanceof File)) return;

                const filename = img?.name || file.name || "image.jpg";
                formData.append("attached_images[]", file, filename);
            });

            const removed = imageHandler.getRemovedExistingImageFilenames?.() || [];
            removed.forEach((name) => {
                if (name) formData.append("removed_attached_images[]", name);
            });
        }

        const editForm = postShowForm.getEditForms(postId)[0] || document;
        const videoManager =
            postShowForm.getVideoManager(editForm, postId) || postShowForm.getVideoManager(document, postId);
        const videoInput = videoManager ? postShowForm.getVideoUploadInput(videoManager) : null;
        const videoFiles = videoInput?.files ? Array.from(videoInput.files) : [];
        videoFiles.forEach((file) => {
            if (file instanceof File) {
                formData.append("attached_videos[]", file, file.name || "video.bin");
            }
        });

        const removedVideosInput = videoManager ? postShowForm.getRemovedAttachedVideosInput(videoManager) : null;
        if (removedVideosInput && removedVideosInput.value) {
            try {
                const removedVideos = JSON.parse(removedVideosInput.value) as JsonValue;
                if (Array.isArray(removedVideos)) {
                    removedVideos.forEach((filename) => {
                        if (filename) formData.append("removed_attached_videos[]", String(filename));
                    });
                }
            } catch (_error) {
                void _error;
            }
        }

        try {
            const response = (await super.formRequest(
                `/api/v1/posts/${boardId}/${String(postId)}`,
                formData,
                "PUT",
            )) as UpdatePostResponse;
            if (!response || typeof response !== "object" || !response.success) {
                throw new Error(
                    response && typeof response === "object" && "message" in response
                        ? String(response.message || "Failed to update post")
                        : "Failed to update post",
                );
            }

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public async deletePost(boardId: string, postId: number): Promise<{ success: boolean; data: DeletePostData }> {
        try {
            const response = PostShowApiService.interceptAuthErrors(
                await super.apiDelete(`/api/v1/posts/${boardId}/${String(postId)}`, { noSpinner: true }),
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = (await response.json()) as DeletePostResponse;

            if (result.success === 1) {
                return {
                    success: true,
                    data: result.data,
                };
            }

            throw new Error("Failed to delete post");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public async likePost(boardId: string, postId: number): Promise<{ success: boolean; data: LikesApiData }> {
        return this.toggleLike(boardId, postId, "like");
    }

    public async unlikePost(boardId: string, postId: number): Promise<{ success: boolean; data: LikesApiData }> {
        return this.toggleLike(boardId, postId, "unlike");
    }

    public async fetchPost(boardId: string, postId: number): Promise<{ success: boolean; data: FetchPostData }> {
        try {
            const response = await super.apiGet(`/api/v1/posts/${boardId}/${String(postId)}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = (await response.json()) as FetchPostResponse;

            if (result.data?.item) {
                return {
                    success: true,
                    data: result.data.item,
                };
            }

            throw new Error("Failed to fetch post");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public async fetchRelatedPosts(
        boardId: string,
        postId: number,
        limit = 5,
    ): Promise<{ success: boolean; data: PostShowApiData }> {
        try {
            const response = await super.apiGet(`/api/v1/posts/${boardId}/${String(postId)}/related?limit=${limit}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = (await response.json()) as BasicApiEnvelope;

            if (result.success) {
                return {
                    success: true,
                    data: result.data,
                };
            }

            throw new Error(result.message || "Failed to fetch related posts");
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public validateEditData(editData: EditPostData): ValidationResult {
        const errors: string[] = [];

        if (!editData.post_title || String(editData.post_title).trim().length === 0) {
            errors.push("title_required");
        } else if (String(editData.post_title).trim().length > 255) {
            errors.push("title_too_long");
        }

        if (!editData.content || String(editData.content).trim().length === 0) {
            errors.push("content_required");
        } else if (String(editData.content).trim().length > 50000) {
            errors.push("content_too_long");
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    public isReadRecorded(): boolean {
        return this.readRecorded;
    }

    public resetReadStatus(): void {
        this.readRecorded = false;
    }

    private async toggleLike(
        boardId: string,
        postId: number,
        mode: "like" | "unlike",
    ): Promise<{ success: boolean; data: LikesApiData }> {
        try {
            const response = PostShowApiService.interceptAuthErrors(
                mode === "like"
                    ? await super.apiPost(`/api/v1/posts/${boardId}/${String(postId)}/likes`, null, {
                          noSpinner: true,
                      })
                    : await super.apiDelete(`/api/v1/posts/${boardId}/${String(postId)}/likes`, {
                          noSpinner: true,
                      }),
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = (await response.json()) as LikePostResponse | UnlikePostResponse;
            if (result.success !== 1) {
                throw new Error("Failed to update like");
            }

            return {
                success: true,
                data: {
                    likes_count: result.likes_count,
                },
            };
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
}

export { PostShowApiService };
